/**
 * The four stages, and the three names each one has (MAR-219).
 *
 * Mum Hub says the same stage three different ways, and none of the three is
 * wrong:
 *
 * 1. an ID, `first-trimester | second-trimester | third-trimester | new-mum`,
 *    which is what `stage.ts` derives from a due date because that is how the
 *    clinical arithmetic reads;
 * 2. a content SLUG, `trimester-1 | trimester-2 | trimester-3 | new-mum`,
 *    which is what the content database seeds and what goes in the URL of
 *    `GET /api/mum-hub/stages/:slug`. Only `new-mum` happens to agree with the
 *    id;
 * 3. a Stripe PRODUCT KEY, `trimester1 | trimester2 | trimester3 | newmum`,
 *    which is what `POST /api/mum-hub/checkout` takes.
 *
 * Left implicit, the first mismatch is a 404 for three customers out of four,
 * and a 404 on that endpoint looks exactly like "Maria has not published this
 * stage yet". The second is a 400 `unknown_product` at the moment she tries to
 * pay. So the three vocabularies meet here, once, in a table read out of
 * `shared/mum-hub-landing.json` so the prerenderer's Product schema and this
 * module cannot disagree about which stages exist.
 *
 * Nothing else may build one of these strings by hand.
 */

import landing from "@shared/mum-hub-landing.json";

export type MumHubStageId =
  | "first-trimester"
  | "second-trimester"
  | "third-trimester"
  | "new-mum";

/** The keys `POST /api/mum-hub/checkout` accepts. */
export type MumHubProductKey =
  | "trimester1"
  | "trimester2"
  | "trimester3"
  | "newmum"
  | "journey"
  | "journey_from_t2"
  | "journey_from_t3"
  | "journey_upgrade";

export interface MumHubStageDescriptor {
  id: MumHubStageId;
  /** Content slug, the `:slug` in the stage endpoint. */
  slug: string;
  /** Stripe product key for this stage on its own. */
  productKey: MumHubProductKey;
  /** Short stage name, sentence case. */
  label: string;
  /** One line on what the stage asks of a wardrobe. Landing page only. */
  body: string;
}

/**
 * The four stages, in order.
 *
 * Read from the shared JSON and then checked, rather than trusted: the file is
 * data and a typo in it would otherwise become a stage slug that 404s or a
 * product key that 400s, at runtime, in front of a customer. The assertions
 * below run at module load, so a bad edit fails the first test that imports
 * anything from Mum Hub rather than the checkout.
 */
export const MUM_HUB_STAGES: readonly MumHubStageDescriptor[] =
  landing.stages.items.map((item) => ({
    id: item.id as MumHubStageId,
    slug: item.slug,
    productKey: item.productKey as MumHubProductKey,
    label: item.label,
    body: item.body,
  }));

const EXPECTED_IDS: readonly MumHubStageId[] = [
  "first-trimester",
  "second-trimester",
  "third-trimester",
  "new-mum",
];

if (MUM_HUB_STAGES.length !== EXPECTED_IDS.length) {
  throw new Error("shared/mum-hub-landing.json must list exactly four stages");
}
MUM_HUB_STAGES.forEach((stage, index) => {
  if (stage.id !== EXPECTED_IDS[index]) {
    throw new Error(
      `shared/mum-hub-landing.json stage ${index} must be ${EXPECTED_IDS[index]}`,
    );
  }
});

/** The Stripe product key for the whole journey. */
export const MUM_HUB_JOURNEY_KEY: MumHubProductKey = "journey";

/**
 * The packs that sell the rest of HER journey (MAR-133).
 *
 * `journey_from_t2` is the second trimester onward and `journey_from_t3` the
 * third. Which of them, if either, a customer is offered is decided by the
 * Worker from the stages still ahead of her and unowned, and this module never
 * works that out: two sides deciding what may be sold is how a website comes to
 * offer a discount an app refuses to honour.
 *
 * Spelled here anyway because they are WIRE values. `isMumHubProductKey` gates
 * a key arriving in a URL, and a pack key it did not recognise would silently
 * fail to preselect the row she followed a link to.
 */
export const MUM_HUB_JOURNEY_FROM_T2_KEY: MumHubProductKey = "journey_from_t2";
export const MUM_HUB_JOURNEY_FROM_T3_KEY: MumHubProductKey = "journey_from_t3";

/**
 * The retired upgrade key.
 *
 * `active = 0` in the Worker's catalogue since migration 0139, so a current
 * Worker never offers it. It stays here because a key is how a purchase
 * somebody already made is still readable, and because the fallback ladder in
 * `offers.ts` has to be able to describe what an undeployed Worker sent.
 */
export const MUM_HUB_JOURNEY_UPGRADE_KEY: MumHubProductKey = "journey_upgrade";

/** Every product key the checkout endpoint accepts, packs included. */
export const MUM_HUB_PRODUCT_KEYS: readonly MumHubProductKey[] = [
  ...MUM_HUB_STAGES.map((stage) => stage.productKey),
  MUM_HUB_JOURNEY_KEY,
  MUM_HUB_JOURNEY_FROM_T2_KEY,
  MUM_HUB_JOURNEY_FROM_T3_KEY,
  MUM_HUB_JOURNEY_UPGRADE_KEY,
];

/** Every content slug, in stage order. */
export const MUM_HUB_STAGE_SLUGS: readonly string[] = MUM_HUB_STAGES.map(
  (stage) => stage.slug,
);

/** The descriptor for a derived stage. Total by construction. */
export function stageDescriptor(id: MumHubStageId): MumHubStageDescriptor {
  const found = MUM_HUB_STAGES.find((stage) => stage.id === id);
  // Unreachable while the module-load check above holds, and thrown rather
  // than defaulted so that a fifth stage added to the JSON and not to the id
  // union fails here instead of resolving to the first trimester.
  if (!found) throw new Error(`Unknown Mum Hub stage: ${id}`);
  return found;
}

/** The content slug for a derived stage. */
export function apiSlugForStage(id: MumHubStageId): string {
  return stageDescriptor(id).slug;
}

/**
 * The derived stage id for a content slug, or null.
 *
 * A real inverse rather than a second hand-written map: two maps that had to
 * agree would be the mismatch this module exists to prevent, written twice.
 * Null for anything that is not one of the four, so a bad link produces a page
 * with no stage rather than a label for a stage that does not exist.
 */
export function stageForApiSlug(slug: string): MumHubStageId | null {
  return MUM_HUB_STAGES.find((stage) => stage.slug === slug)?.id ?? null;
}

/** The stage a product key buys, or null for the two journey offers. */
export function stageForProductKey(key: string): MumHubStageId | null {
  return MUM_HUB_STAGES.find((stage) => stage.productKey === key)?.id ?? null;
}

/** Whether a string is a product key the checkout endpoint accepts. */
export function isMumHubProductKey(value: string): value is MumHubProductKey {
  return (MUM_HUB_PRODUCT_KEYS as readonly string[]).includes(value);
}
